import React from 'react';
import PropTypes from 'prop-types';
import {withTheme} from '@elixir/theme';
import {FieldComponent} from '../FieldComponent';
import styles from './InputField-hashed.scss';
import cx from 'classnames';
import { isEmptyValue, getValueForInput } from '../fieldValues';
import getIconClasses from '../../../iconUtils';

/**
 * InputField is a 'field' component that displays a label, control, and message. It can be used to render
 * any HTML5 <input> element.
 */
export class InputField extends FieldComponent {

    static propTypes = {
        ...FieldComponent.propTypes,
        /**
         * HTML5 input type. e.g. 'password'
         */
        inputType: PropTypes.string,
        /**
         * Max length for the input field
         */
        maxLength: PropTypes.number,
        /**
        * Phrase which indicates where the icon should be placed relative to the text
        **/
        iconPlacement: PropTypes.oneOf(['left', 'right'])
    };

    static defaultProps = {
        ...FieldComponent.defaultProps,
        iconPlacement: 'left'
    };

    // explicitly defined due to docgen limitations
    render() {
        return super.render();
    }

    /**
     * Get the validation messages to display.
     * If field is 'required' and no validation message is provided, it is added
     * If field is 'number' and have max and min value and no validation message is provided, it is added
     *
     * @returns {Array} validation messages
     */
    getValidationMessages() {
        const {
            value,
            inputType,
            maxValue,
            minValue
        } = this.props;
        const superValidationMessages = super.getValidationMessages() || [];

        if (inputType === 'number' && !isEmptyValue(value)) {
            const { translator } = this.context;
            if (Number(value) < Number(minValue)) {
                return [...superValidationMessages, translator({
                    id: 'policycommon.The minimum allowed value is',
                    defaultMessage: 'The minimum allowed value is {{value}}'
                }, { value: minValue })];
            }
            if (Number(value) > Number(maxValue)) {
                return [...superValidationMessages, translator({
                    id: 'policycommon.The maximum allowed value is',
                    defaultMessage: 'The maximum allowed value is {{value}}'
                }, { value: maxValue })];
            }
        }

        return superValidationMessages;
    }

    /**
     * Default change handler for `input` element. It will be used unless this method or `renderControl()`
     * is overridden.
     *
     * @param {Object} evt - React event wrapper
     */
    handleChange = evt => {
        let value = evt.target.value;
        // TBD: handle this with input mask instead
        if (typeof value === 'string' && this.props.autoTrim) {
            value = value.trim();
        }

        const { inputType } = this.props;

        if (inputType === 'number') {
            const pattern = new RegExp(/^-?(\s*|\d+\.?\d*)$/);

            if (!pattern.test(value) && !isEmptyValue(value)) {
                return;
            }
        }

        this.notifyChange(value);
    };


    /**
     * Specific onKeyDown handler function for number inputs
     *
     * @param {Object} event - React event wrapper
     */
    handleKeyDownForNumberInput = (event) => {
        const { value } = this.props;
        let numberValue;
        if (!isEmptyValue(value)) {
            numberValue = Math.floor(Number(value));
            if (Number.isNaN(numberValue)) {
                numberValue = 0;
            }
        } else {
            numberValue = 0;
        }

        // if the arrow up button is pressed increase the input value by 1
        if (event.keyCode === 38) {
            this.notifyChange(numberValue + 1);
            event.preventDefault();
        }

        // if the arrow down button is pressed descrease the input value by 1
        if (event.keyCode === 40) {
            this.notifyChange(numberValue - 1);
            event.preventDefault();
        }
    }

    /**
     * Render control for this component.
     *
     * @param {string} inputType - HTML5 input type
     * @param {object} options - rendering options (like 'isInvalid', 'isValid', etc)
     *
     * @returns {ReactElement} JSX for the control
     */
    renderControl(inputType, options) {
        const {
            theme, id, value, placeholder, maxLength, disabled, controlClassName, required
        } = this.props;
        const { translator } = this;
        const focusHandlers = this.getInputFocusHandlers();

        const themeStyles = theme.getStyles('Input', styles);
        const themeProps = theme.getProps('Input', this.props);
        const { icon, iconPlacement } = themeProps;
        const inputStyle = cx(
            themeStyles.input,
            {
                disabled,
                invalid: !options.isValid
            },
            controlClassName
        );
        const numberProps = inputType === 'number' ? {
            type: 'text',
            onKeyDown: this.handleKeyDownForNumberInput
        } : {};

        const inputElement = (
            <input
                id={id}
                type={inputType}
                className={inputStyle}
                placeholder={translator(placeholder)}
                maxLength={maxLength}
                value={getValueForInput(inputType, value)}
                onChange={this.handleChange}
                {...focusHandlers}
                disabled={disabled}
                required={required}
                {...this.generateDataPathProperty()}
                {...numberProps}
            />
        );

        if (icon && iconPlacement) {
            const iconElement = <i className={cx('fa-fw', getIconClasses(icon), { disabled })}></i>;

            return (
                <div className={themeStyles.inputWithIcon}>
                    {iconPlacement === 'left' && iconElement}
                    {inputElement}
                    {iconPlacement === 'right' && iconElement}
                </div>
            );
        }

        return inputElement;
    }
}

export default withTheme(InputField);
